<template>
  <div class="px-5 pb-5 pt-2">
    <div class="discovery-header">
      <h2 class="top-title mb-4">Explore Your Digital Risk</h2>
    </div>
    <ScanList
      :list="scanList"
      :scans="sortList"
      :compare-id="compareFrom"
      :compare-to-id="compareTo"
      :loading="isLoadingComparison || isLoadingAnalysis"
      :compareMode="compareMode"
      :discoveryType="discoveryType"
      :selectedScan="selectedScan"
      :selectedDrives="selectedDrives"
      :scan="scan"
      :drivesName="drivesName"
      :isSelectAll="isSelectAll"
      :showFuture="showFuture"
      @showSunburst="showSunburst"
      @showHistogram="showHistogram"
      @showStackedArea="showStackedArea"
      @handleChangeDrives="handleChangeDrives"
      @remove="remove"
      @selectAll="selectAll"
      @confirmExportModal="confirmExportModal"
      @onSelect="handleScanSelect"
    />
    <v-row v-if="discoveryType == 'histogram'">
      <v-col :md="'12'" cols="12">
        <p class="main-subtitle mt-2">
          Explore By Attribute
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon color="#212338" size="22" style="top: -2px" class="ml-1" light v-bind="attrs" v-on="on">
                mdi-help-circle-outline
              </v-icon>
            </template>
            <p><span>Click any bar in the charts below to filter your selection. </span><br /></p>
          </v-tooltip>
        </p>
        <span class="fs-16"> Current Selection </span>
        <div class="d-flex flex-row mt-2">
          <div class="discovery-card">
            <p class="discovery-card__title">Volume</p>
            <p class="discovery-card__pr">{{ totalSize }}</p>
          </div>
          <div class="discovery-card ml-7">
            <p class="discovery-card__title">File Count</p>
            <p class="discovery-card__pr">{{ countSum }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <template v-if="discoveryType == 'histogram'">
      <template v-if="!compareMode">
        <template>
          <v-row>
            <v-col sm="12" lg="6">
              <v-card class="dark-primary-bg explore-card">
                <v-toolbar color="primary" dark class="shadow-none">
                  <v-card-title color="primary" class="second-subtitle pl-0 d-flex flex-row">
                    Explore by Size
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="#212338" size="22" class="ml-3" light v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <p>
                        <span>What's the distribution of file volumes?</span>
                      </p>
                    </v-tooltip>
                  </v-card-title>
                </v-toolbar>
                <filters
                  v-if="sizeFilters.length"
                  chartId="size"
                  filterId="sizeFilters"
                  :filters="sizeFilters"
                  :labels="getSizeLabels"
                  @removeFilter="removeFilter"
                />
                <Histogram
                  id="size"
                  v-if="size.length"
                  :discoveryId="compareFrom"
                  :scanStartTime="scanStartTime"
                  filterId="sizeFilters"
                  :scanEndTime="scanEndTime"
                  :drives="selectedDrives"
                  :xAxix="getSizeLabels"
                  :yAxix="getSizeValues"
                  :data="size"
                  xAxisKey="label"
                  yAxisKey="total_count"
                  :height="460"
                  width="inherit"
                />
                <no-data-found v-else :customStyle="{ height: 390 + 'px' }" />
              </v-card>
            </v-col>
            <v-col style="height: 430px" sm="12" lg="6">
              <v-card class="dark-primary-bg explore-card">
                <v-toolbar color="primary" dark class="shadow-none">
                  <v-card-title class="second-subtitle pl-0 d-flex flex-row space-between">
                    Explore by Age
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="#212338" size="22" class="ml-3" light v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <p>
                        <span>How much stale data is in production? </span><br />
                        <span>Are we actually enforcing our retention policies?</span>
                      </p>
                    </v-tooltip>
                  </v-card-title>
                </v-toolbar>
                <filters
                  v-if="lastModifiedFilters.length"
                  chartId="age"
                  filterId="lastModifiedFilters"
                  :filters="lastModifiedFilters"
                  :labels="getLast_modifiedLabels"
                  @removeFilter="removeFilter"
                />
                <Histogram
                  id="age"
                  v-if="last_modified.length"
                  :discoveryId="compareFrom"
                  :scanStartTime="scanStartTime"
                  filterId="lastModifiedFilters"
                  :scanEndTime="scanEndTime"
                  :drives="selectedDrives"
                  :xAxix="getLast_modifiedLabels"
                  :yAxix="getLast_modifiedValues"
                  :data="getLast_modified"
                  xAxisKey="label"
                  yAxisKey="total_count"
                  :height="460"
                  width="inherit"
                />
                <no-data-found v-else :customStyle="{ height: 390 + 'px' }" />
              </v-card>
            </v-col>
            <v-col class="pt-0 mt-5">
              <v-card class="dark-primary-bg explore-card">
                <v-toolbar color="primary" dark class="shadow-none">
                  <v-card-title class="second-subtitle pl-0"
                    >Explore by Type
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="#212338" size="22" class="ml-3" light v-bind="attrs" v-on="on">
                          mdi-help-circle-outline
                        </v-icon>
                      </template>
                      <p>
                        <span>What types of files do we store?</span>
                      </p>
                    </v-tooltip>
                  </v-card-title>
                </v-toolbar>
                <filters
                  v-if="extensionFilters.length"
                  chartId="file_extension"
                  filterId="extensionFilters"
                  :filters="extensionFilters"
                  @removeFilter="removeFilter"
                />
                <Histogram
                  id="file_extension"
                  v-if="extension.length"
                  :discoveryId="compareFrom"
                  filterId="extensionFilters"
                  :scanStartTime="scanStartTime"
                  :scanEndTime="scanEndTime"
                  :drives="selectedDrives"
                  :xAxix="extensionLabels"
                  :yAxix="extensionCounts"
                  :data="topExtentions"
                  xAxisKey="extension"
                  yAxisKey="total_count"
                  :height="460"
                  width="inherit"
                />
                <no-data-found v-else :customStyle="{ height: 390 + 'px' }" />
              </v-card>
            </v-col>
          </v-row>
        </template>
      </template>
      <v-row v-else-if="compareMode && comparisonList">
        <v-col sm="12" lg="12">
          <v-card class="dark-primary-bg explore-card">
            <v-toolbar color="primary" dark class="shadow-none">
              <v-card-title class="top-title">Size</v-card-title>
            </v-toolbar>
            <StackedBarChart
              v-if="comparisonList.size.length"
              :data="comparisonList.size"
              key="size_compare"
              id="size_compare"
              :height="450"
              width="inherit"
            />
            <no-data-found v-else :customStyle="{ height: 390 + 'px' }" />
          </v-card>
        </v-col>
        <v-col sm="12" lg="12">
          <v-card class="dark-primary-bg explore-card">
            <v-toolbar color="primary" dark class="shadow-none">
              <v-card-title class="top-title">Extension</v-card-title>
            </v-toolbar>
            <StackedBarChart
              v-if="comparisonList.extension.length"
              :data="comparisonList.extension"
              key="extension_compare"
              id="extension_compare"
              :height="450"
              width="inherit"
              sort
            />
            <no-data-found v-else :customStyle="{ height: 390 + 'px' }" />
          </v-card>
        </v-col>
      </v-row>
    </template>
    <div v-if="discoveryType == 'discovery'" class="d-flex flex-row justify-start mt-8">
      <p class="main-subtitle">Explore by Risk Segment</p>
      <div class="mr-10">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="#212338" size="22" class="ml-2 mt-1" light v-bind="attrs" v-on="on">
              mdi-help-circle-outline
            </v-icon>
          </template>
          <p>
            <span
              >Mouse-over segment of the diagram to learn more about that segment. <br /><br />Click on a segment to
              focus the diagram on that segment.</span
            ><br />
          </p>
        </v-tooltip>
      </div>
    </div>
    <div v-if="discoveryType == 'discovery' && !isLoadingScans && !isLoadingAnalysis">
      <v-row>
        <v-col sm="12" lg="12">
          <SunBurst
            :windowWidth="windowWidth * 0.8 > 850 ? 850 : windowWidth * 0.8"
            :size="size"
            :extension="extension"
            :last_modified="getLast_modified"
          />
        </v-col>
      </v-row>
    </div>
    <div v-if="discoveryType == 'stackedArea' && showFuture">
      <TimeSeries
        :discoveryId="compareFrom"
        :windowWidth="windowWidth*0.72"
        @setTimeSeriesDates="setTimeSeriesDates"
      />
    </div>
    <div
      v-if="
        (isLoadingAnalysis && !size && !last_modified && !extension) ||
        isLoadingScans ||
        (isLoadingComparison && !comparisonList)
      "
      class="text-center py-10"
    >
      <v-progress-circular :size="70" color="primary" indeterminate></v-progress-circular>
    </div>
    <!--    Confirm download discovery dialog-->
    <v-dialog v-model="confirmExportDialog.open" persistent max-width="650">
      <v-card class="delete-dialog">
        <div v-if="!confirmExportDialog.isExportingDiscovery">
          <v-card-title class="headline dark--text">Export Options</v-card-title>
          <v-card-text class="dark--text text-left pb-0">Records included in export</v-card-text>
          <v-card-text class="pt-0">
            <v-radio-group v-model="confirmExportDialog.exportType">
              <v-radio label="Filtered" value="filter"></v-radio>
              <v-radio label="All" value="all"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-text class="dark--text text-left py-0">Limit export by record count</v-card-text>
          <v-card-text class="pt-0">
            <v-radio-group row v-model="confirmExportDialog.limit">
              <v-radio label="No Limit" value="all"></v-radio>
              <v-radio label="5k" :value="5000"></v-radio>
              <v-radio label="100k" :value="100000"></v-radio>
              <v-radio label="1m" :value="1000000"></v-radio>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="cancel-btn" text @click="confirmExportDialog.open = false">Cancel</v-btn>
            <v-btn
              class="save-btn"
              text
              @click="confirmExportDialog.action(confirmExportDialog.argument)"
              :disabled="confirmExportDialog.isExportingDiscovery"
              >Confirm</v-btn
            >
          </v-card-actions>
        </div>
        <div v-else-if="!confirmExportDialog.downloadUrl || loading" class="text-center">
          <v-progress-circular :size="70" color="primary" indeterminate></v-progress-circular>
          <div class="dark--text pt-3">Generating your export...</div>
        </div>
        <div v-else-if="confirmExportDialog.downloadUrl" class="text-center">
          Export complete, <a @click="exportChannelDownloadApi" class="download-link">click to download</a>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import Filters from '@/components/Discovery/Filters'
import ScanList from '@/components/Discovery/ScanList'
import Histogram from '@/components/Discovery/Histogram'
import StackedBarChart from '@/components/Discovery/StackedBarChart'
import SunBurst from '@/components/Discovery/SunBurst'
import TimeSeries from '@/components/Discovery/TimeSeries'
import NoDataFound from '@/components/Global/NoDataFound'
import moment from 'moment'
import { convert, formattedDateTime } from '@/utils/main_helper'
import collectorMixin from '@/mixins/collectorMixin'
import api from 'Api'
import pusher from '@/pusher'
import { uuid } from 'vue-uuid'
import exportJsonToCSV from '../mixins/exportJsonToCSV'

const showFuture = process.env.VUE_APP_SHOW_FUTURE == 'true'
export default {
  name: 'Discovery',
  mixins: [collectorMixin, exportJsonToCSV],
  components: {
    ScanList,
    Filters,
    Histogram,
    StackedBarChart,
    SunBurst,
    TimeSeries,
    NoDataFound
  },
  computed: {
    ...mapState('discoveryServices', [
      'size',
      'last_modified',
      'extension',
      'scanList',
      'isLoadingAnalysis',
      'isLoadingScans',
      'comparisonList',
      'isLoadingComparison',
      'sizeFilters',
      'lastModifiedFilters',
      'extensionFilters',
      'labelIndexs',
      'discoveryId',
      'isLoadingAnalysis',
      'isLoadingScans',
      'chartId'
    ]),
    ...mapGetters(['currentOrganization', 'user']),
    ...mapGetters('discoveryServices', {
      _labelIndexs: 'labelIndexs'
    }),
    ...mapGetters({ getTimeSeries: 'discoveryServices/getTimeSeries' }),
    totalSize () {
      const totalSize = this[this.getOtherChartID(this.chartId)]
        .map((s) => Number(s.total_size))
        .reduce((a, b) => {
          return a + b
        }, 0)
      return convert(totalSize, 'GB', 1000)
    },
    countSum () {
      const sum = this[this.getOtherChartID(this.chartId)]
        .map((s) => Number(s.total_count))
        .reduce((a, b) => {
          return a + b
        }, 0)
      return convert(sum, 'count', 1000)
    },
    getSizeLabels () {
      return this.size && this.size.map((size) => size.label)
    },
    getSizeValues () {
      return this.size && this.size.map((size) => Number(size.total_count))
    },
    getLast_modifiedLabels () {
      return (
        this.last_modified &&
        this.last_modified.map((lastModified) =>
          this.labelsMap[lastModified.label] ? this.labelsMap[lastModified.label] : lastModified.label
        )
      )
    },
    getLast_modifiedValues () {
      return this.last_modified && this.last_modified.map((lastModified) => lastModified.total_count)
    },
    getLast_modified () {
      return (
        this.last_modified &&
        this.last_modified.map((lm) => {
          lm.label = this.labelsMap[lm.label] ? this.labelsMap[lm.label] : lm.label
          return lm
        })
      )
    },
    sortList () {
      let filteredList = []
      const collectors = []
      this.scanList.map((e) => {
        e.total_files && filteredList.push(e)
        if (e.collector_id && !collectors.includes(e.collector_id)) {
          collectors.push(e.collector_id)
        }
      })
      filteredList = filteredList.sort((a, b) => {
        // sort empty scan_start_time scans at bottom
        if (!a.scan_start_time) {
          return 1
        } else if (!b.scan_start_time) {
          return -1
        }
        // ... and sort the remaining scans by scan_start_time
        return (
          moment(b.scan_start_time, 'YYYY-MM-DD hh:mm:ss A').valueOf() -
          moment(a.scan_start_time, 'YYYY-MM-DD hh:mm:ss A').valueOf()
        )
      })
      if (collectors.length > 1) {
        const startTime = moment.utc(filteredList[0].scan_end_time, 'YYYY-MM-DD hh:mm:ss A').subtract(24, 'hours').format('X')
        const endTime = moment.utc(filteredList[0].scan_end_time, 'YYYY-MM-DD hh:mm:ss A').format('X')
        filteredList.unshift({
          value: 'Last 24 Hours',
          scan_end_time: endTime,
          scan_start_time: startTime,
          entity_id: 'last_24_hours'
        })
      }
      return filteredList
    },
    isSelectAll () {
      return this.scan?.drives?.length == this.selectedDrives.length
    }
  },
  data () {
    return {
      showFuture: showFuture,
      scan: { drives: [] },
      compareFrom: undefined,
      scanStartTime: undefined,
      scanEndTime: undefined,
      compareTo: undefined,
      compareMode: false,
      discoveryType: '',
      isHistogram: false,
      windowWidth: window.innerWidth,
      labelsMap: {
        '< 1yr old': '< 1',
        '>= 1 and < 2 yrs': '1 - 2',
        '>= 2 and < 3 yrs': '2 - 3',
        '>= 3 and < 4 yrs': '3 - 4',
        '>= 4 and < 5 yrs': '4 - 5',
        '>= 5 and < 6 yrs': '5 - 6',
        '>= 6 and < 7 yrs': '6 - 7',
        '>= 7yrs': '> 7',
        '>= 7 and < 8 yrs': '7 - 8',
        '>= 8 and < 9 yrs': '8 - 9',
        '>= 9 and < 10 yrs': '9 - 10',
        '>= 10yrs': '> 10'
      },
      selectedDrives: [],
      topExtentions: [],
      extensionLabels: [],
      extensionCounts: [],
      selectedScan: null,
      drivesName: {},
      confirmExportDialog: {
        open: false,
        name: '',
        text: '',
        argument: '',
        action: '',
        downloadUrl: null,
        isExportingDiscovery: false,
        exportType: 'filter',
        limit: 'all',
        records_count: 0
      },
      timeSeriesDates: [],
      loading: false
    }
  },
  created () {
    if (this.discoveryId) {
      this.compareFrom = this.discoveryId
    }
    this.unwatchOrganization = this.$store.watch(
      (state, getters) => getters.currentOrganization,
      (newValue) => {
        if (newValue) {
          this.requestScanList()
        }
      }
    )
  },
  methods: {
    ...mapMutations('discoveryServices', ['resetAllFilters', 'resetAnalysis']),
    selectRecentScan () {
      const query = this.$route.query
      let scanT = null
      let isQueryParams = false
      if (query && query.organization_id && query.discovery_id) {
        const scans = this.scanList.filter((scan) => scan.entity_id == query.discovery_id)
        scanT = scans.length ? scans[0] : null
        isQueryParams = query.drive && query.drive.length > 0
      }
      if (!scanT) {
        scanT = this.sortList && this.sortList?.[0]
      }
      if (scanT) {
        if (scanT.entity_id === 'last_24_hours') {
          this.scan = {
            entity_id: 'last_24_hours',
            scan_start_time: scanT.scan_start_time,
            scan_end_time: scanT.scan_end_time,
            type: 'last_24_hours',
            value: 'Last 24 Hours'
          }
        } else {
          this.scan = {
            organization_id: scanT.organization_id,
            entity_id: scanT.entity_id,
            scan_start_time: scanT.scan_start_time,
            completed_on: scanT.completed_on,
            drives: this.getDrivesArray(scanT.drive_statistics, scanT.completed_on, scanT.collector_id, scanT.provider)
          }
          this.drivesName = this.getDrivesName(scanT.collector_id)
          this.scan.drives.map((drive) => {
            if (this.selectedDrives.includes(drive.name)) {
              drive.checked = true
            }
          })
        }
        this.selectedScan = Object.assign({}, this.scan)
        this.compareFrom = this.scan.entity_id
        this.scanStartTime = this.scan.scan_start_time
        this.scanEndTime = this.scan.scan_end_time
        if (!isQueryParams) {
          this.selectAll(true)
        }
      }
    },
    handleScanSelect ({ item, compare }) {
      this.selectedScan = item
      this.selectedDrives = []
      if (item.entity_id === 'last_24_hours') {
        this.scan = {
          entity_id: 'last_24_hours',
          scan_start_time: item.scan_start_time,
          scan_end_time: item.scan_end_time,
          type: 'last_24_hours',
          value: 'Last 24 Hours'
        }
      } else {
        this.scan = {
          organization_id: item.organization_id,
          entity_id: item.entity_id,
          completed_on: item.completed_on,
          scan_start_time: item.scan_start_time,
          drives: this.getDrivesArray(item.drive_statistics, item.completed_on, item.collector_id, item.provider)
        }
        this.drivesName = this.getDrivesName(item.collector_id)
      }

      this.compareFrom = item.entity_id
      if (compare === 'from') {
        this.compareFrom = item.entity_id
        this.resetAllFilters()
        // this.requestDiscoveryAnalysis(entityId, localDrives)
      } else if (compare === 'to') {
        this.compareTo = item.entity_id
      }

      let compareFroml, compareTol
      if (
        this.compareFrom != undefined &&
        this.compareFrom != 'last_24_hours' &&
        this.compareTo != undefined &&
        this.compareTo != this.compareFrom
      ) {
        this.scanList.map((m) => {
          if (m.entity_id == this.compareFrom) {
            compareFroml = m
          } else if (m.entity_id == this.compareTo) {
            compareTol = m
          }
        })
        if (
          moment(compareFroml.scan_start_time, 'YYYY-MM-DD hh-mm A').valueOf() -
            moment(compareTol.scan_start_time, 'YYYY-MM-DD hh-mm A').valueOf() >
          0
        ) {
          this.$store.dispatch('discoveryServices/getComparison', {
            organization_id: this.currentOrganization.entity_id,
            compareId: compareTol.entity_id,
            compareToId: compareFroml.entity_id
          })
        } else {
          this.$store.dispatch('discoveryServices/getComparison', {
            organization_id: this.currentOrganization.entity_id,
            compareId: compareFroml.entity_id,
            compareToId: compareTol.entity_id
          })
        }
        this.compareMode = true
      }
      this.selectAll(true)
    },
    requestScanList () {
      this.$store
        .dispatch('discoveryServices/getScansList', { organization_id: this.currentOrganization.entity_id })
        .then(() => {
          this.selectRecentScan()
        })
      showFuture && this.$store
        .dispatch('discoveryServices/timeSeries', { organization_id: this.currentOrganization.entity_id })
    },
    requestDiscoveryAnalysis (discoveryScanId, drives) {
      if (discoveryScanId == 'last_24_hours') {
        this.$store.dispatch('discoveryServices/getAnalysisByStartEndTime', {
          organization_id: this.currentOrganization.entity_id,
          scan_start_time: this.scan.scan_start_time,
          scan_end_time: this.scan.scan_end_time
        })
      } else {
        this.$store.dispatch('discoveryServices/getAnalysis', {
          organization_id: this.currentOrganization.entity_id,
          discovery_id: discoveryScanId,
          drives
        })
      }
    },
    removeFilter (data) {
      let payload = {}
      if (this.scan.entity_id == 'last_24_hours') {
        payload = {
          organization_id: this.currentOrganization.entity_id,
          start_datetime: this.scan.scan_start_time,
          end_datetime: this.scan.scan_end_time,
          filterLabels: {
            size: [],
            age: [],
            file_extension: []
          }
        }
      } else {
        payload = {
          organization_id: this.currentOrganization.entity_id,
          discovery_id: this.compareFrom,
          filterLabels: {
            size: [],
            age: [],
            file_extension: []
          }
        }
      }
      if (this.sizeFilters.length) {
        payload.size = this.sizeFilters.map((s) => this.labelIndexs.size[s])
        payload.filterLabels.size = this.sizeFilters
      }
      if (this.lastModifiedFilters.length) {
        payload.age = this.lastModifiedFilters.map((s) => this.labelIndexs.age.indexOf(s))
        payload.filterLabels.age = this.lastModifiedFilters
      }
      if (this.extensionFilters.length) {
        payload.file_extension = this.extensionFilters
        payload.filterLabels.file_extension = this.extensionFilters
      }
      if (this.selectedDrives.length) payload.drive = this.selectedDrives
      payload.chartId = this.realChartId(data.field)

      if (payload.chartId) {
        this.$store.dispatch('discoveryServices/filterDiscoveryBars', { payload })
      }
    },
    realChartId (id) {
      if (id === 'file_extension' && this.extensionFilters.length) {
        return 'extension'
      } else if (id === 'age' && this.lastModifiedFilters.length) {
        return 'last_modified'
      } else if (id === 'size' && this.sizeFilters.length) {
        return 'size'
      } else if (this.extensionFilters.length) {
        return 'extension'
      } else if (this.lastModifiedFilters.length) {
        return 'last_modified'
      } else if (this.sizeFilters.length) {
        return 'size'
      }
      return 'size'
    },
    showHistogram () {
      this.isHistogram = true
      this.resetAllFilters()
      this.discoveryType = 'histogram'
      this.$router.push('/discovery/histogram')
    },
    showSunburst () {
      this.resetSunburst()
      this.discoveryType = 'discovery'
      this.isHistogram = false
      this.$router.push('/discovery')
    },
    showStackedArea () {
      this.isHistogram = true
      this.resetAllFilters()
      if (this.selectedDrives.length) {
        this.selectedDrives = []
        this.selectRecentScan()
        this.requestDiscoveryAnalysis(this.compareFrom, this.selectedDrives)
      }
      this.discoveryType = 'stackedArea'
      this.$router.push('/discovery/stackedArea')
    },
    onResize () {
      this.windowWidth = window.innerWidth
    },
    remove (item) {
      item.checked = false
      const index = this.selectedDrives.indexOf(item.name)
      if (index >= 0) this.selectedDrives.splice(index, 1)
      this.handleSelectDrives(this.selectedDrives)
    },
    selectAll (checked) {
      this.selectedDrives = []
      if (this.scan.entity_id !== 'last_24_hours') {
        this.scan.drives.map((item) => {
          item.checked = checked
          if (checked) {
            this.selectedDrives.push(item.name)
          }
        })
      }
      this.handleSelectDrives(this.selectedDrives)
    },
    handleChangeDrives (drives) {
      this.selectedDrives = drives
      this.handleSelectDrives(drives)
    },
    handleSelectDrives (drives) {
      const payload = {
        organization_id: this.currentOrganization.entity_id,
        filterLabels: {
          size: [],
          age: [],
          file_extension: []
        }
      }
      if (this.scan.entity_id == 'last_24_hours') {
        payload.start_datetime = this.scan.scan_start_time
        payload.end_datetime = this.scan.scan_end_time
      } else {
        payload.discovery_id = this.compareFrom
      }

      if (this.sizeFilters.length) {
        payload.size = this.sizeFilters.map((s) => this.labelIndexs.size[s])
        payload.filterLabels.size = this.sizeFilters
      }
      if (this.lastModifiedFilters.length) {
        payload.age = this.lastModifiedFilters.map((s) => this.labelIndexs.age.indexOf(s))
        payload.filterLabels.age = this.lastModifiedFilters
      }
      if (this.extensionFilters.length) {
        payload.file_extension = this.extensionFilters
        payload.filterLabels.file_extension = this.extensionFilters
      }
      if (drives.length) payload.drive = drives
      this.$store.dispatch('discoveryServices/filterDiscoveryBars', {
        payload,
        deleteOnInsertion: false,
        redirect: true
      })
    },
    getDrivesArray (statistices, completed_on, collector_id, provider) {
      const drives = []
      if (statistices) {
        for (const key of Object.keys(statistices)) {
          drives.push({
            name: key,
            completed_on,
            total_files: statistices[key].total_count,
            collectorName: this.getCollectorName(collector_id),
            provider: provider
          })
        }
      }
      return drives
    },
    resetSunburst () {
      if (
        this.extensionFilters.length ||
        this.sizeFilters.length ||
        this.lastModifiedFilters.length
      ) {
        this.selectedDrives = []
        this.resetAnalysis()
        this.resetAllFilters()
        this.$store
          .dispatch('discoveryServices/getScansList', { organization_id: this.currentOrganization.entity_id })
          .then(() => {
            this.selectRecentScan()
            this.requestDiscoveryAnalysis(this.compareFrom, this.selectedDrives)
          })
      }
    },
    confirmExportModal (modal) {
      this.confirmExportDialog = {
        open: true,
        text: modal.text,
        name: modal.name ? item[modal.name] : '',
        action: this.discoveryType == 'stackedArea' ? this.timeSeriesCSV : this.downloadDiscovery,
        downloadUrl: null,
        isExportingDiscovery: false,
        exportType: 'filter',
        limit: 'all'
      }
    },
    resetconfirmExportDialog () {
      this.confirmExportDialog = {
        open: false,
        name: '',
        text: '',
        argument: '',
        action: '',
        downloadUrl: null,
        isExportingDiscovery: false,
        exportType: 'filter',
        limit: 'all',
        records_count: 0
      }
    },
    async downloadDiscovery () {
      const self = this
      const exportId = uuid.v4()
      let isFilter = false
      const exportChannel = pusher.subscribe(exportId)
      this.confirmExportDialog.isExportingDiscovery = true
      this.confirmExportDialog.downloadUrl = null
      exportChannel.bind('export-available', (data) => {
        self.confirmExportDialog.downloadUrl = data?.url
        self.confirmExportDialog.records_count = data?.records_count
        this.addExportLog()
      })
      const payload = {
        organization_id: this.currentOrganization.entity_id,
        export_id: exportId
      }
      if (this.scan.entity_id == 'last_24_hours') {
        payload.start_datetime = this.scan.scan_start_time
        payload.end_datetime = this.scan.scan_end_time
      } else {
        payload.discovery_id = this.compareFrom
      }

      if (this.confirmExportDialog.limit !== 'all') {
        payload.limit = this.confirmExportDialog.limit
      }

      if (this.confirmExportDialog.exportType !== 'all') {
        if (this.sizeFilters.length) {
          payload.size = this.sizeFilters.map((s) => parseInt(this._labelIndexs.size[s]))
          isFilter = true
        }
        if (this.lastModifiedFilters.length) {
          payload.age = this.lastModifiedFilters.map((s) => this.labelIndexs.age.indexOf(s))
          isFilter = true
        }
        if (this.extensionFilters.length) {
          payload.file_extension = this.extensionFilters
          isFilter = true
        }
      }
      if (this.selectedDrives.length) payload.drive = this.selectedDrives
      const fileName = this.getFileName(isFilter, this.scan)
      payload.filename = fileName
      try {
        await api().post('/discovery/generate-discovery-export', payload)
      } catch (e) {}
    },
    async timeSeriesCSV () {
      if (!this.showFuture) return
      this.loading = true
      this.confirmExportDialog.isExportingDiscovery = true
      var headers = {
        date: 'Date'.replace(/,/g, ''),
        business_continuity_count: 'Business Continuity Count',
        manage_govern_count: 'Manage & Govern Count',
        risk_management_count: 'Risk Management Count',
        business_continuity_size: 'Business Continuity Size (B)',
        manage_govern_size: 'Manage & Govern Size (B)',
        risk_management_size: 'Risk Management Size (B)'
      }
      const minTime = moment(this.timeSeriesDates[0]).valueOf()
      const maxTime = moment(this.timeSeriesDates[1]).valueOf()
      let timeSeriesKeys = []
      let timeSeriesData = this.getTimeSeries
      if (this.confirmExportDialog.exportType != 'all') {
        timeSeriesKeys = Object.keys(timeSeriesData).filter((d) => {
          return minTime <= moment(d, 'YYYY-MM-DD').valueOf() && maxTime >= moment(d, 'YYYY-MM-DD').valueOf()
        })
      } else {
        const res = await this.$store.dispatch('discoveryServices/timeSeries', { organization_id: this.currentOrganization.entity_id, stateless: true })
        if (res.data) {
          timeSeriesData = res.data
          timeSeriesKeys = Object.keys(timeSeriesData)
        }
      }

      const itemsFormatted = []
      let count = 0
      for (const key of timeSeriesKeys) {
        if (this.isRecordEmpty(timeSeriesData[key])) continue
        count++
        itemsFormatted.push({
          date: key.replace(/,/g, ''), // remove commas to avoid errors
          business_continuity_count: timeSeriesData[key].business_continuity.count,
          manage_govern_count: timeSeriesData[key].manage_govern.count,
          risk_management_count: timeSeriesData[key].risk_management.count,
          business_continuity_size: timeSeriesData[key].business_continuity.size,
          manage_govern_size: timeSeriesData[key].manage_govern.size,
          risk_management_size: timeSeriesData[key].risk_management.size
        })
        if (this.confirmExportDialog.limit != 'all' && count >= this.confirmExportDialog.limit) break
      }

      const filename = `discovery-export-${this.confirmExportDialog.exportType != 'all' ? 'filtered' : 'unfiltered'}-`
      const payload = {
        organization_id: this.currentOrganization.entity_id,
        user_id: this.user.user_id,
        number_of_records: itemsFormatted.length,
        datetime: moment().unix(),
        source: 'Discovery'
      }
      const data = await this.$store.dispatch('addExport', payload)
      data && itemsFormatted.length && this.exportCSVFile(headers, itemsFormatted, filename)
      this.loading = false
      this.resetconfirmExportDialog()
    },
    setTimeSeriesDates (dates) {
      this.timeSeriesDates = dates
    },
    exportChannelDownloadApi () {
      const self = this
      self.$mixpanel.track('Export discovery xlsx file')
      window.open(self.confirmExportDialog.downloadUrl, '_blank')
      self.resetconfirmExportDialog()
    },
    getOtherChartID (chartId) {
      const chartIds = ['size', 'extension', 'last_modified']
      return chartIds.filter((id) => id != chartId)[0]
    },
    getFileName (isFilter, scan) {
      let filename = `discovery-export-${isFilter ? 'filtered' : 'unfiltered'}-`
      filename =
        filename +
        (scan.entity_id == 'last_24_hours'
          ? moment(Number(scan.scan_start_time) * 1000).format('YYYY-MM-DD')
          : formattedDateTime(scan.scan_start_time, 'YYYY-MM-DD'))
      return filename
    },
    async addExportLog () {
      const payload = {
        organization_id: this.currentOrganization.entity_id,
        user_id: this.user.user_id,
        number_of_records: this.confirmExportDialog.records_count,
        datetime: moment().unix(),
        source: 'Discovery'
      }
      this.loading = true
      await this.$store.dispatch('addExport', payload)
      this.loading = false
    },
    isRecordEmpty (scan) {
      return !scan.business_continuity.count && !scan.risk_management.count && !scan.risk_management.count &&
      !scan.business_continuity.size && !scan.risk_management.size && !scan.risk_management.size
    }
  },
  beforeDestroy () {
    this.unwatchOrganization = ''
    window.removeEventListener('resize', this.onResize)
    this.resetAllFilters()
  },
  mounted () {
    const drives = this.$route.query.drive
    this.selectedDrives = drives ? (drives.includes(',') ? drives.split(',') : [drives]) : []
    this.discoveryType = this.$route.name
    this.isHistogram = this.$route.name === 'histogram'
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize)
    })
    if (this.scanList.length) {
      this.selectRecentScan()
    }
  },
  watch: {
    scanList () {
      this.selectRecentScan()
    },
    extension (newValue) {
      const labels = []
      const counts = []
      this.topExtentions = newValue
        .filter((ex) => (newValue.length > 20 ? ex.extension !== '' : ex))
        .sort((a, b) => {
          return b.total_count - a.total_count
        })
        .slice(0, 20)
      this.topExtentions.map((ext) => {
        labels.push(ext.extension)
        counts.push(ext.total_count)
      })
      this.extensionLabels = labels
      this.extensionCounts = counts
    }
  }
}
</script>

<style lang="scss">
.delete-chip {
  .v-icon {
    color: $primary-color;
  }
}
.border-bottom {
  border-bottom: 1px solid #434153;
}

.discovery-header {
  margin-top: 10px;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
}
.v-toolbar__content,
.v-toolbar__extension {
  justify-content: space-between;
}
.discovery-card {
  position: relative;
  background: linear-gradient(0deg, #eceff3 0%, #ffffff 118.98%);
  overflow: hidden;
  border-radius: 12px;
  padding: 16px 22px 8px 25px;
  margin-bottom: 5px;
  width: 250px;

  .discovery-card__title {
    font-weight: 700;
    font-size: 13px;
    line-height: 13px;
    color: $black-color;
    margin-bottom: 0px;
    height: 30px;
    overflow: hidden;
    text-align: left;
  }

  .discovery-card__pr {
    font-weight: 700;
    font-size: 24px;
    line-height: 0.2;
    color: $black-color;
    text-align: left;
  }
}

.explore-card {
  border: none !important;

  .v-toolbar__content {
    padding-left: 0px;
  }
}

.discovery-view-icon {
  cursor: pointer;
  filter: brightness(0) saturate(100%) invert(11%) sepia(8%) saturate(3528%) hue-rotate(197deg) brightness(90%)
    contrast(90%);
  &:hover {
    filter: grayscale(0);
  }
  &--active {
    filter: grayscale(0);
  }
}
</style>
